import { SearchRequest, Sort } from '@elastic/elasticsearch/lib/api/types';
import { GridSortModel } from '@mui/x-data-grid';
import { intervalToDuration } from 'date-fns';

import { CALL_ANALYSIS_EVENT_TYPE } from '@/data/contact-center';
import { CallFlowItem, ConversationHistoryGeneralProps } from '@/domain/bot';
import { StickerType } from '@/domain/sticker-module-types';
import { ANALYTIC_EVENTS_PATH } from '@/utils/api/elastic/analytic-events-path';

export type condition = {
    [key: string]: string;
};

export function buildGroupedSessionsRequest(
    condition: condition,
    sessionIds: string[],
    size?: string,
    sortingDetails?: GridSortModel,
): SearchRequest {
    const sortCriteria = buildSortCriteria(sortingDetails);
    const [sortField, sortOrder] = Object.entries(sortCriteria)[0];
    const shouldSortByDuration = sortField === 'session_duration';

    return {
        size: 0,
        query: {
            bool: {
                must: [
                    { wildcard: condition },
                    { terms: { 'session_id.keyword': sessionIds } },
                ],
            },
        },
        aggs: {
            groupedSessions: {
                terms: {
                    field: 'session_id.keyword',
                    size: Number(size) || 100,
                    ...(shouldSortByDuration
                        ? {}
                        : {
                              order: {
                                  [sortField]: sortOrder,
                              },
                          }),
                },
                aggs: {
                    sessionDetails: {
                        top_hits: {
                            size: 50,
                            sort: [{ '@timestamp': { order: 'asc' } }],
                        },
                    },
                    latest_analysis: {
                        filter: {
                            term: {
                                [`${ANALYTIC_EVENTS_PATH}.type.keyword`]:
                                    CALL_ANALYSIS_EVENT_TYPE,
                            },
                        },
                        aggs: {
                            hit: {
                                top_hits: {
                                    size: 1,
                                    sort: [{ '@timestamp': { order: 'desc' } }],
                                    _source: [ANALYTIC_EVENTS_PATH],
                                },
                            },
                        },
                    },
                    earliest_timestamp: {
                        min: { field: '@timestamp' },
                    },
                    latest_timestamp: {
                        max: { field: '@timestamp' },
                    },
                    session_duration: {
                        bucket_script: {
                            buckets_path: {
                                min_time: 'earliest_timestamp',
                                max_time: 'latest_timestamp',
                            },
                            script: 'params.max_time - params.min_time',
                        },
                    },
                    ...(shouldSortByDuration
                        ? {
                              sort_by_duration: {
                                  bucket_sort: {
                                      sort: [
                                          {
                                              session_duration: {
                                                  order: sortOrder,
                                              },
                                          },
                                      ],
                                      size: Number(size) || 100,
                                  },
                              },
                          }
                        : {}),
                },
            },
        },
    };
}

const padLeft: (n?: number) => string = (n) => {
    if (!n) {
        return '00';
    }
    if (n <= 9) {
        return '0' + n;
    }
    return n.toString();
};

export async function parseSessionBucket(
    bucket: any,
): Promise<ConversationHistoryGeneralProps> {
    const key = bucket.key;
    const sessionHits = bucket.sessionDetails.hits.hits.map(
        (hit: { _source: any }) => hit._source,
    );
    const validHitTimesMs = sessionHits
        .map((hit: { '@timestamp': string | number | Date }) =>
            new Date(hit['@timestamp']).getTime(),
        )
        .filter((ms: number) => !Number.isNaN(ms));
    // One invalid @timestamp must not poison Math.min → NaN for the whole session.
    const sessionStartMs =
        validHitTimesMs.length > 0 ? Math.min(...validHitTimesMs) : Number.NaN;

    const getTimeOffsetSeconds = (hit: {
        '@timestamp': string | number | Date;
    }) => {
        const hitMs = new Date(hit['@timestamp']).getTime();
        if (Number.isNaN(hitMs) || Number.isNaN(sessionStartMs)) {
            return undefined;
        }
        return (hitMs - sessionStartMs) / 1000;
    };

    const callFlowItems: CallFlowItem[] = [];
    const flowItemsForHistoryReport: string[] = [];
    const analyticEvents: unknown[][] = [];
    const userModels = [];
    let hangupReason: string | undefined;
    let mchannelsId: string | undefined;
    let phoneNumber: string | undefined;
    let testUser: boolean = false;

    for (const item of sessionHits) {
        const timeOffsetSeconds = getTimeOffsetSeconds(item);
        const hitDate = new Date(item['@timestamp']);
        const occurredAt = Number.isNaN(hitDate.getTime())
            ? undefined
            : hitDate.toISOString();

        if (
            item?.input?.analytic_events &&
            // add iteration rather instead of analytic_events[0]
            item?.input?.analytic_events[0]?.type === 'widget_interaction' &&
            item?.input?.analytic_events[0]?.subtype === 'click'
        ) {
            callFlowItems.push({
                type: 'widget_interaction',
                data: item?.input?.analytic_events[0]?.data,
                timeOffsetSeconds,
                occurredAt,
            });

            const widgetLabel =
                item?.input?.analytic_events[0]?.data?.label || 'Interaction';
            flowItemsForHistoryReport.push(`User: ${widgetLabel}`);
        }

        if (item?.bot?.mchannels_id) {
            mchannelsId = item?.bot?.mchannels_id;
        }

        if (item?.user_info?.phone_number) {
            phoneNumber = item?.user_info?.phone_number;
        }

        if (item?.user_info?.is_test_user) {
            testUser = item?.user_info?.is_test_user;
        }

        if (item?.input?.text) {
            callFlowItems.push({
                type: 'text',
                direction: '=>',
                text: item.input.text,
                timeOffsetSeconds,
                occurredAt,
            });

            flowItemsForHistoryReport.push(
                `User: ${safeStripHtml(item.input.text)}`,
            );
        }

        if (item?.output?.generic) {
            for (const generic of item.output.generic) {
                if (generic.response_type === 'hyperlink') {
                    callFlowItems.push({
                        type: 'hyperlink',
                        preference: generic.preference,
                        url: generic.url,
                        text: generic.text,
                        timeOffsetSeconds,
                        occurredAt,
                    });

                    flowItemsForHistoryReport.push(
                        `Link: ${generic.text || generic.url}`,
                    );
                }
                if (generic.response_type === 'option') {
                    callFlowItems.push({
                        type: 'options',
                        options: generic.options,
                        timeOffsetSeconds,
                        occurredAt,
                    });

                    const optionTexts = (generic.options || [])
                        .map((option: any) =>
                            safeStripHtml(option.value?.input?.text || ''),
                        )
                        .filter(Boolean)
                        .join(', ');
                    flowItemsForHistoryReport.push(`Options: ${optionTexts}`);
                }
                if (generic.response_type === 'text') {
                    callFlowItems.push({
                        type: 'text',
                        direction: '<=',
                        text: generic.text,
                        timeOffsetSeconds,
                        occurredAt,
                    });

                    flowItemsForHistoryReport.push(
                        `Bot: ${safeStripHtml(generic.text)}`,
                    );
                }
                if (generic.response_type === 'non_utterance') {
                    callFlowItems.push({
                        type: 'non_utterance',
                        direction: '<=',
                        text: generic.text,
                        timeOffsetSeconds,
                        occurredAt,
                    });
                    // no reporting: do not push to flowItemsForHistoryReport
                }
                if (
                    generic.response_type === 'user_model' &&
                    generic.data?.conversation_metrics?.closing_reason
                ) {
                    hangupReason =
                        generic.data?.conversation_metrics?.closing_reason;
                }
                if (generic.response_type === 'user_model') {
                    userModels.push(generic);
                }
            }
        }

        if (item?.output?.analytic_events) {
            analyticEvents.push(item.output.analytic_events);
        }
    }

    const latestAnalysisEvents =
        bucket.latest_analysis?.hit?.hits?.hits?.[0]?._source?.output
            ?.analytic_events;
    if (latestAnalysisEvents) {
        analyticEvents.push(latestAnalysisEvents);
    }
    const stickers: StickerType[] = [];
    if (userModels.length > 0) {
        const lastUserModel = userModels.at(-1);
        if (lastUserModel.data?.stickers?.length > 0) {
            stickers.push(...lastUserModel.data.stickers);
        }
    }

    const timestamps = sessionHits.map(
        (x: { [x: string]: string | number | Date }) =>
            new Date(x['@timestamp']),
    );
    const duration = intervalToDuration({
        start: new Date(Math.min(...timestamps)),
        end: new Date(Math.max(...timestamps)),
    });

    return {
        id: key,
        sessionId: key,
        turnCount:
            Number(
                sessionHits
                    .filter(
                        (hit: { turn_id: null | undefined }) =>
                            hit.turn_id !== null && hit.turn_id !== undefined,
                    )
                    .at(-1)?.turn_id || 0,
            ) + 1,
        startedAt: new Date(Math.min(...timestamps))
            .toISOString()
            .replace('T', ' ')
            .replace(/\.[0-9]+Z$/, ''),
        duration: `${padLeft(duration.hours)}:${padLeft(
            duration.minutes,
        )}:${padLeft(duration.seconds)}`,
        callFlowItems,
        flowItemsForHistoryReport,
        stickers,
        analyticEvents,
        mchannelsId,
        phoneNumber: phoneNumber ?? '',
        testUser: testUser,
        endReason: hangupReason,
    };
}
export async function parseSessionBucketForDashboard(bucket: any) {
    const key = bucket.key;
    const sessionHits = bucket.sessionDetails.hits.hits.map(
        (hit: { _source: any }) => hit._source,
    );

    const stickers = [];
    for (const item of sessionHits) {
        // Inside your loop over sessionHits
        if (item?.output?.generic) {
            for (const generic of item.output.generic) {
                if (generic.response_type === 'user_model') {
                    const conversationStickers = generic.data?.stickers;
                    if (conversationStickers?.length > 0) {
                        stickers.push(
                            conversationStickers[
                                conversationStickers.length - 1
                            ],
                        );
                    }
                }
            }
        }

        const timestamps = sessionHits.map(
            (x: { [x: string]: string | number | Date }) =>
                new Date(x['@timestamp']),
        );
        const duration = intervalToDuration({
            start: new Date(Math.min(...timestamps)),
            end: new Date(Math.max(...timestamps)),
        });

        return {
            id: key,
            sessionId: key,
            turnCount: sessionHits.length,
            startedAt: new Date(Math.min(...timestamps))
                .toISOString()
                .replace('T', ' ')
                .replace(/\.[0-9]+Z$/, ''),
            duration: `${padLeft(duration.hours)}:${padLeft(
                duration.minutes,
            )}:${padLeft(duration.seconds)}`,
            stickers,
        };
    }
}

export const parseConversationBucket = async (bucket: any) => {
    const sessionId = bucket.key;
    const startedAt = new Date(bucket.session_first.value).toISOString();
    const duration = bucket.session_duration.value;

    let stickers: StickerType[] = [];
    if (
        bucket.non_empty_docs &&
        bucket.non_empty_docs.last_populated_doc.hits.hits.length > 0
    ) {
        const lastDoc = bucket.non_empty_docs.last_populated_doc.hits.hits[0];
        if (
            lastDoc._source &&
            lastDoc._source.output &&
            lastDoc._source.output.generic
        ) {
            const genericData = lastDoc._source.output.generic;
            if (
                Array.isArray(genericData) &&
                genericData.length > 0 &&
                genericData[0].data &&
                genericData[0].data.stickers
            ) {
                stickers = genericData[0].data.stickers.map(
                    (sticker: StickerType) => ({
                        name: sticker.name,
                        description: sticker.description || '',
                        appearance: {
                            color: sticker.appearance?.color || '',
                        },
                    }),
                );
            }
        }
    }

    return {
        id: sessionId,
        sessionId,
        startedAt,
        duration,
        stickers,
        analyticEvents: [
            bucket.analysis_docs?.last_analysis_doc?.hits?.hits?.[0]?._source
                ?.output?.analytic_events,
        ].filter(Boolean),
    };
};

function durationToSeconds(durationStr: string) {
    const [h, m, s] = durationStr.split(':').map(Number);
    return h * 3600 + m * 60 + s;
}

function safeStripHtml(html: string): string {
    if (!html) return '';

    return html.replace(/<[^>]*>?/gm, '');
}

export function simplifyConversations(jsonData: any, isChat: boolean) {
    const simplifiedCalls: any = [];

    for (const call of jsonData.calls || []) {
        const duration = call.duration;
        let simplifiedCall: any = {
            sessionId: call.sessionId,
            ...(isChat ? {} : { phoneNumber: call.phoneNumber }),
            testUser: call.testUser,
            turnCount: call.turnCount,
            startedAt: call.startedAt,
            duration: duration,
            flowItems: call.flowItemsForHistoryReport || [],
            stickers: call.stickers
                ? call.stickers.map((sticker: StickerType) => sticker.name)
                : [],
            'duration (in sec)': duration ? durationToSeconds(duration) : 0,
        };
        if (!isChat) simplifiedCall.endReason = call.endReason;
        simplifiedCalls.push(simplifiedCall);
    }
    return simplifiedCalls;
}

export const buildSortCriteria = (
    sortingDetails?: GridSortModel,
    isVoiceBot?: boolean,
): Record<string, 'asc' | 'desc'> => {
    if (!sortingDetails || sortingDetails.length === 0) {
        return { earliest_timestamp: 'desc' };
    }

    const sortItem = sortingDetails[0];

    const fieldMapping: Record<string, string> = {
        startedAt: 'earliest_timestamp',
        duration: 'session_duration',
        // doc count used, during elastic unification refactor to turn count as more precise
        turn_count: isVoiceBot ? 'doc_count' : '_count',
    };

    const field = sortItem.field as string;
    const mappedField = fieldMapping[field] || 'earliest_timestamp';
    const order = sortItem.sort === 'asc' ? 'asc' : 'desc';
    return { [mappedField]: order };
};
